import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box, Checkbox, Chip, FormControlLabel, Grid, IconButton, InputAdornment, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { CalendarMonth, Download } from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import CsvDownloader from "react-csv-downloader";
import moment from 'moment';
import { authorizedPost, toggleLoading } from '../../store/actions';
import { LIST_ADVANCE_RECEIPT_REPORTS } from '../../graphql';
import { PaginationFooter } from '../../components';

const allModes = [
    { name: "Cash", value: "cash" },
    { name: "Card", value: "card" },
    { name: "UPI", value: "upi" },
    { name: "Bank transfer", value: "bank_transfer" },
    { name: "Cheque", value: "cheque" },
    { name: "OTA", value: "ota" },
];

const getModeName = (mode) => allModes.find((m) => m.value === mode)?.name || mode || "-";
const formatUnix = (value) => (value ? moment.unix(value).format("YYYY-MM-DD") : "-");
const formatMillis = (value) => (value ? moment(value).format("YYYY-MM-DD") : "-");

function AdvanceReceiptReport({ colors }) {
    const dispatch = useDispatch();
    const startInputRef = useRef();
    const endInputRef = useRef();
    const [receipts, setReceipts] = useState([]);
    const [totals, setTotals] = useState({ total_amount: 0, mode_totals: [] });
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [modes, setModes] = useState([]);
    const [includeVoided, setIncludeVoided] = useState(false);
    const [allReceipts, setAllReceipts] = useState(null);
    const [pagination, setPagination] = useState({
        count: 1,
        pageSize: 50,
        pageNumber: 1,
    });

    const buildRequest = () => {
        let req = {
            mode: modes.join(),
            includeVoided,
        };
        if (startDate && endDate) {
            req.startDate = startDate;
            req.endDate = endDate;
        }
        return req;
    }

    const fetchReceipts = async (page) => {
        dispatch(toggleLoading(true));
        let req = {
            ...buildRequest(),
            pageNumber: 1,
            pageSize: pagination.pageSize,
            ...page,
        };
        let response = await authorizedPost(LIST_ADVANCE_RECEIPT_REPORTS, req);
        if (response?.data?.list_advance_receipt_reports) {
            const { data, count, total_amount, mode_totals } = response.data.list_advance_receipt_reports;
            setReceipts(data || []);
            setTotals({ total_amount: total_amount || 0, mode_totals: mode_totals || [] });
            setPagination({
                count: parseInt(count) || 0,
                pageNumber: req.pageNumber,
                pageSize: req.pageSize,
            });
        }
        dispatch(toggleLoading(false));
    }

    useEffect(() => {
        fetchReceipts();
        // eslint-disable-next-line
    }, [startDate, endDate, modes, includeVoided])

    const fetchAllReceipts = async () => {
        dispatch(toggleLoading(true));
        let response = await authorizedPost(LIST_ADVANCE_RECEIPT_REPORTS, {
            ...buildRequest(),
            pageNumber: 1,
            pageSize: pagination.count || 1,
        });
        if (response?.data?.list_advance_receipt_reports) {
            setAllReceipts(response.data.list_advance_receipt_reports.data || []);
            setTimeout(() => {
                const csvDownloader = document.getElementById('advance-receipt-csv-downloader');
                if (csvDownloader) {
                    csvDownloader.click();
                }
            }, 100);
        }
        dispatch(toggleLoading(false));
    }

    const generateCsvDownloadData = (data) => data.map((row) => ({
        "Receipt no": row.receipt_no,
        "Receipt date": formatMillis(row.date),
        "Booking id": row.booking_id,
        "Guest name": row.guest_name || "",
        "Check-in date": formatUnix(row.checkin_date),
        "Check-out date": formatUnix(row.checkout_date),
        "Mode": getModeName(row.mode),
        "Amount": row.amount,
        "Collected by": row.collected_by || "",
        "Transaction ref": row.transaction_ref || "",
        "Bank name": row.bank_name || "",
        "Remarks": row.remarks || "",
        "Status": row.is_active ? "Active" : "Voided",
    }));

    const CustomFooter = () => (
        <PaginationFooter
            count={pagination.count}
            pageSize={pagination.pageSize}
            pageNumber={pagination.pageNumber}
            fetchMoreData={(page) => fetchReceipts({ pageNumber: pagination.pageNumber, ...page })}
        />
    );

    const columns = [
        { field: "receipt_no", headerName: "Receipt no", width: 110 },
        {
            field: "date",
            headerName: "Receipt date",
            width: 115,
            valueGetter: (params) => formatMillis(params.row.date),
        },
        { field: "booking_id", headerName: "Booking id", width: 120 },
        {
            field: "guest_name",
            headerName: "Guest info",
            width: 200,
            renderCell: ({ row: { guest_name, guest_mobile } }) => (
                <Stack>
                    <Typography color={colors.greenAccent[400]}>
                        <b>{guest_name || "-"}</b>
                    </Typography>
                    <Typography fontSize={"small"}>
                        {guest_mobile || ""}
                    </Typography>
                </Stack>
            ),
        },
        {
            field: "checkin_date",
            headerName: "Check-in date",
            width: 120,
            valueGetter: (params) => formatUnix(params.row.checkin_date),
        },
        {
            field: "checkout_date",
            headerName: "Check-out date",
            width: 120,
            valueGetter: (params) => formatUnix(params.row.checkout_date),
        },
        {
            field: "mode",
            headerName: "Mode",
            width: 120,
            valueGetter: (params) => getModeName(params.row.mode),
        },
        { field: "amount", headerName: "Amount", width: 100 },
        {
            field: "collected_by",
            headerName: "Collected by",
            width: 130,
            valueGetter: (params) => params.row.collected_by || "-",
        },
        {
            field: "transaction_ref",
            headerName: "Transaction ref",
            width: 140,
            valueGetter: (params) => params.row.transaction_ref || "-",
        },
        {
            field: "is_active",
            headerName: "Status",
            width: 110,
            renderCell: ({ row: { is_active } }) => (
                <Chip size="small" variant="outlined" label={is_active ? "Active" : "Voided"} color={is_active ? "success" : "error"} />
            ),
        },
    ];

    return (
        <Box mt={5}>
            <Typography variant="h4" fontWeight="bold" mb={2}>
                Advance Receipt Report
            </Typography>
            <Grid container spacing={2} justifyContent="space-between">
                <Grid item>
                    <Stack direction={"row"} spacing={1} alignItems="center">
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Receipt start date"
                            sx={{ width: 190 }}
                            InputLabelProps={{ shrink: true }}
                            value={startDate ? moment.unix(startDate).format("YYYY-MM-DD") : ""}
                            onChange={(e) => setStartDate(e.target.value ? moment(e.target.value, "YYYY-MM-DD").startOf("day").unix() : "")}
                            inputRef={startInputRef}
                            onClick={() => startInputRef.current.showPicker()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth sx={{ cursor: "pointer" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                        <TextField
                            fullWidth
                            size="small"
                            type="date"
                            label="Receipt end date"
                            sx={{ width: 190 }}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ min: startDate ? moment.unix(startDate).format("YYYY-MM-DD") : undefined }}
                            value={endDate ? moment.unix(endDate).format("YYYY-MM-DD") : ""}
                            onChange={(e) => setEndDate(e.target.value ? moment(e.target.value, "YYYY-MM-DD").endOf("day").unix() : "")}
                            inputRef={endInputRef}
                            onClick={() => endInputRef.current.showPicker()}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CalendarMonth sx={{ cursor: "pointer" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Stack>
                </Grid>
                <Grid item>
                    <Stack direction={"row"} spacing={2} alignItems="center">
                        <FormControlLabel
                            control={
                                <Checkbox
                                    size="small"
                                    checked={includeVoided}
                                    onChange={(e) => setIncludeVoided(e.target.checked)}
                                />
                            }
                            label="Show voided"
                        />
                        <TextField
                            select
                            size="small"
                            variant="outlined"
                            label="Mode"
                            value={modes}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value.includes("all")) {
                                    setModes(modes.length === allModes.length ? [] : allModes.map(m => m.value));
                                } else {
                                    setModes(value);
                                }
                            }}
                            sx={{ width: 120 }}
                            SelectProps={{ multiple: true, renderValue: (selected) => selected.map(getModeName).join(", ") }}
                        >
                            <MenuItem value={"all"}>
                                <Checkbox
                                    size="small"
                                    sx={{ p: "0 5px 0 0" }}
                                    checked={modes.length === allModes.length}
                                    indeterminate={modes.length > 0 && modes.length < allModes.length}
                                />
                                <em>All</em>
                            </MenuItem>
                            {allModes.map((e) => (
                                <MenuItem value={e.value} key={e.value}>
                                    <Checkbox size="small" sx={{ p: "0 5px 0 0" }} checked={modes.indexOf(e.value) > -1} />
                                    {e.name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <IconButton
                            title="Export as CSV"
                            sx={{ borderRadius: "5px", bgcolor: colors.greenAccent[700] }}
                            onClick={fetchAllReceipts}
                        >
                            <Download />
                        </IconButton>
                        {allReceipts && (
                            <CsvDownloader
                                datas={generateCsvDownloadData(allReceipts)}
                                filename={`Advance_receipt_report - ${localStorage.getItem("hotelName") ?? ""} - ${moment().format("YYYY-MM-DD hh-mm-ss")}`}
                            >
                                <button id="advance-receipt-csv-downloader" style={{ display: 'none' }}>
                                    Download CSV
                                </button>
                            </CsvDownloader>
                        )}
                    </Stack>
                </Grid>
            </Grid>
            <Box
                m="8px 0 0 0"
                width="100%"
                height="70vh"
                sx={{
                    "& .MuiDataGrid-root": {
                        fontSize: 16,
                    },
                    "& .MuiDataGrid-columnHeaders": {
                        backgroundColor: colors.blueAccent[700],
                        borderBottom: "none",
                    },
                    "& .MuiDataGrid-virtualScroller": {
                        backgroundColor: colors.primary[400],
                    },
                    "& .MuiDataGrid-footerContainer": {
                        borderTop: "none",
                        backgroundColor: colors.blueAccent[700],
                    },
                }}
            >
                <DataGrid
                    rows={receipts}
                    columns={columns}
                    components={{ Footer: CustomFooter }}
                    getRowId={(row) => row?._id}
                    pageSize={100}
                />
            </Box>
            <Box mt={2} p={0.5} border={"1px solid grey"}>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-evenly" flexWrap="wrap">
                    {totals.mode_totals.map((e) => (
                        <Typography key={e.mode}>
                            {getModeName(e.mode)} ({e.count}) &nbsp;&nbsp; <b>{e.amount}</b>
                        </Typography>
                    ))}
                    <Typography>Total Amount &nbsp;&nbsp; <b>{totals.total_amount}</b></Typography>
                </Stack>
            </Box>
        </Box>
    )
}

export default AdvanceReceiptReport;
